import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { WeeklyBriefMarketSnapshot } from "@/services/weeklyBriefService";
import { SectionLabel } from "./SectionLabel";
import { QuoteIndexCard, QuoteSparkline, QuoteTickerRow } from "@/components/markets/QuoteCards";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

const compact = (n: number) =>
  n >= 1e12 ? `$${(n / 1e12).toFixed(2)}T` : n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : `$${n.toLocaleString()}`;

const SEMI_ARC_LENGTH = Math.PI * 40; // radius 40 half circle

function Stat({
  label,
  value,
  sub,
  tone,
  spark,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "up" | "down";
  spark?: number[];
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-2 font-display text-2xl font-semibold tabular-nums text-foreground">{value}</div>
      {sub && (
        <div
          className={`mt-1 text-sm tabular-nums ${
            tone === "up" ? "text-success" : tone === "down" ? "text-destructive" : "text-muted-foreground"
          }`}
        >
          {sub}
        </div>
      )}
      {spark && spark.length > 1 && <QuoteSparkline points={spark} tone={tone === "down" ? "down" : "up"} />}
    </div>
  );
}

/** Matches the thresholds used by the live market stats banner. */
function fgColor(value: number) {
  if (value >= 75) return "hsl(var(--success))";
  if (value >= 55) return "hsl(var(--primary))";
  if (value >= 45) return "hsl(var(--muted-foreground))";
  if (value >= 25) return "hsl(var(--warning))";
  return "hsl(var(--destructive))";
}

function FearGreedGauge({ value, classification }: { value: number; classification: string }) {
  const clamped = Math.max(0, Math.min(100, value));
  // 0 -> 180deg (left), 50 -> 270deg (up), 100 -> 360deg (right)
  const rad = ((180 + (clamped / 100) * 180) * Math.PI) / 180;
  const color = fgColor(clamped);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"><T k="auto.fear-amp-greed-6b78" /></div>
      <div className="flex items-center gap-3">
        <svg viewBox="0 0 100 62" className="h-20 w-28">
          <path
            d="M 10 55 A 40 40 0 0 1 90 55"
            fill="none"
            stroke="hsl(var(--muted))"
            strokeWidth="8"
            strokeLinecap="round"
          />
          <path
            d="M 10 55 A 40 40 0 0 1 90 55"
            fill="none"
            stroke={color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${(clamped / 100) * SEMI_ARC_LENGTH} ${SEMI_ARC_LENGTH}`}
          />
          <line
            x1="50"
            y1="55"
            x2={50 + Math.cos(rad) * 30}
            y2={55 + Math.sin(rad) * 30}
            stroke="hsl(var(--foreground))"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="50" cy="55" r="3.5" fill="hsl(var(--foreground))" />
        </svg>
        <div>
          <div className="font-display text-3xl font-semibold tabular-nums" style={{ color }}>
            {clamped}
          </div>
          <div className="text-sm text-muted-foreground">{classification}</div>
        </div>
      </div>
    </div>
  );
}

export function MarketPulse({ snapshot, asOf }: { snapshot: WeeklyBriefMarketSnapshot; asOf?: string | null }) {
  const t = useT();
  if (!snapshot || (snapshot as any).unavailable || snapshot.total_market_cap === undefined) {
    return (
      <section className="mb-16">
        <SectionLabel><T k="auto.market-pulse-ff2f" /></SectionLabel>
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          <T k="auto.market-data-was-unavailable-when-this-is-52ba" />
        </div>
      </section>
    );
  }

  const series = (snapshot.mcap_series ?? []).map(([t, v]) => ({ t, v }));
  const values = series.map((s) => s.v);
  const change = snapshot.market_cap_change_24h ?? 0;
  const first = values[0];
  const last = values[values.length - 1];
  const weekChange = first && last ? ((last - first) / first) * 100 : null;
  const indices = snapshot.indices ?? [];
  const techStocks = snapshot.tech_stocks ?? [];

  const asOfLabel = asOf
    ? new Date(asOf).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <section className="mb-16">
      <SectionLabel><T k="auto.market-pulse-ff2f" /></SectionLabel>
      {asOfLabel && (
        <p className="-mt-2 mb-5 text-xs text-muted-foreground">
          Snapshot taken {asOfLabel}, figures are frozen at issue time, not live prices.
        </p>
      )}

      {(indices.length > 0 || techStocks.length > 0) && (
        <div className="mb-6">
          <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Indices &amp; tech equities
          </div>
          {indices.length > 0 && (
            <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
              {indices.map((ix) => (
                <QuoteIndexCard key={ix.symbol} q={ix} />
              ))}
            </div>
          )}
          {techStocks.length > 0 && (
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              {techStocks.map((s) => (
                <QuoteTickerRow key={s.symbol} q={s} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground"><T k="auto.crypto-e849" /></div>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label={t("auto.market-cap-81c9")}
          value={compact(snapshot.total_market_cap)}
          sub={`${change >= 0 ? "+" : ""}${change.toFixed(2)}% 24h`}
          tone={change >= 0 ? "up" : "down"}
          spark={values}
        />
        <Stat label={t("auto.24h-volume-c4d0")} value={compact(snapshot.total_volume)} />
        <Stat
          label={t("auto.btc-dominance-2a46")}
          value={`${snapshot.btc_dominance?.toFixed(1)}%`}
          sub={`ETH ${snapshot.eth_dominance?.toFixed(1)}%`}
        />
        {snapshot.fear_greed ? (
          <FearGreedGauge value={snapshot.fear_greed.value} classification={snapshot.fear_greed.classification} />
        ) : (
          <Stat label={t("auto.fear-greed-a0e2")} value="-" />
        )}
      </div>

      {series.length > 1 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-4 flex items-baseline justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              7-day total market cap
            </div>
            {weekChange !== null && (
              <div className={`text-sm tabular-nums ${weekChange >= 0 ? "text-success" : "text-destructive"}`}>
                {weekChange >= 0 ? "+" : ""}
                {weekChange.toFixed(2)}% this week
              </div>
            )}
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="mcap" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="t"
                  tickFormatter={(t: number) =>
                    new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  }
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={(v: number) => compact(v)}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                  width={62}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v: number) => [compact(v), "Market cap"]}
                  labelFormatter={(t: number) => new Date(t).toLocaleDateString()}
                />
                <Area type="monotone" dataKey="v" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#mcap)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </section>
  );
}
